/* src/components/testing/TestingPage.js */
import React, { useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faChartLine,
  faBrain,
  faCalculator,
  faBook,
  faArrowRight,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

import { useProfile } from '../../contexts/ProfileContext';
import { formatRelativeTime } from '../../utils/dateUtils';

const TestingPage = () => {
    const navigate = useNavigate();
    const { activeProfile, isLoading: isProfileLoading } = useProfile();

    // Optional: Keep for debugging if needed
    useEffect(() => {
        if (!isProfileLoading && activeProfile) {
            console.log('[TestingPage useEffect] Component received activeProfile update. cstHistory:', JSON.stringify(activeProfile.cstHistory, null, 2));
        } else if (isProfileLoading) {
            // console.log('[TestingPage useEffect] Profile is loading...');
        } else {
            // console.log('[TestingPage useEffect] No active profile.');
        }
    }, [activeProfile, isProfileLoading]);


    const progressData = useMemo(() => {
        const defaults = {
            completedTests: 0,
            lastTest: "N/A",
            improvement: "N/A",
            isLoading: true
        };

        if (isProfileLoading || !activeProfile) {
            return { ...defaults, isLoading: true };
        }

        try {
            const history = activeProfile.cstHistory || [];

            // Filter entries: Must have timestamp, score (using 'scorePCU'),
            // AND be a non-familiarization session (using 'familiarizationSession')
            // *** CHANGE: Use 'scorePCU' and 'familiarizationSession' ***
            const sortedHistory = [...history]
                .filter(entry =>
                    entry.timestamp &&
                    entry.scorePCU !== undefined &&    // <-- Use scorePCU
                    entry.scorePCU !== null &&
                    entry.familiarizationSession === false // <-- Use familiarizationSession
                )
                .sort((a, b) => {
                    const dateA = new Date(a.timestamp);
                    const dateB = new Date(b.timestamp);
                    if (isNaN(dateA) && isNaN(dateB)) return 0;
                    if (isNaN(dateA)) return -1;
                    if (isNaN(dateB)) return 1;
                    return dateA - dateB;
                });

            const completedTests = sortedHistory.length;

            // Determine the timestamp of the last *actual* assessment
            // Note: lastCstAssessmentTimestamp likely *is* updated correctly only on non-familiarization
            const lastTestTimestamp = activeProfile.lastCstAssessmentTimestamp || (completedTests > 0 ? sortedHistory[completedTests - 1]?.timestamp : null);
            const lastTest = formatRelativeTime(lastTestTimestamp);

            // Calculate improvement percentage based only on *actual* assessments
            let improvement = "N/A";
            if (completedTests >= 2) {
                const latest = sortedHistory[completedTests - 1];
                const previous = sortedHistory[completedTests - 2];

                // *** CHANGE: Use 'scorePCU' ***
                const latestScore = latest?.scorePCU;
                const previousScore = previous?.scorePCU;

                if (typeof latestScore === 'number' && typeof previousScore === 'number') {
                    if (previousScore !== 0) {
                        const change = ((latestScore - previousScore) / Math.abs(previousScore)) * 100;
                        if (isFinite(change)) {
                            improvement = `${change >= 0 ? '+' : ''}${change.toFixed(0)}%`;
                        } else {
                             improvement = "Error";
                        }
                    } else if (latestScore > 0) {
                        improvement = "+∞%";
                    } else {
                        improvement = "0%";
                    }
                }
            }

            return { completedTests, lastTest, improvement, isLoading: false };

        } catch (error) {
             console.error("[TestingPage useMemo] Error calculating progress data:", error); // Keep error log
             return { ...defaults, isLoading: false, lastTest: "Error", improvement: "Error" };
        }

    }, [activeProfile, isProfileLoading]);

    // Helper function to render the content of a progress card
    const renderProgressContent = (value, isPositiveImprovement = false, isNegativeImprovement = false) => {
        if (progressData.isLoading) {
            return <FontAwesomeIcon icon={faSpinner} spin className="text-gray-500 h-5 w-5" />;
        }
        let textColor = 'text-gray-800';
        if (isPositiveImprovement) textColor = 'text-green-600';
        if (isNegativeImprovement) textColor = 'text-red-600';
        return <div className={`text-xl font-semibold ${textColor}`}>{value}</div>;
    };

    // Determine color flags for improvement
    const isPositive = progressData.improvement?.startsWith('+') && progressData.improvement !== "N/A";
    const isNegative = progressData.improvement?.startsWith('-') && progressData.improvement !== "N/A";


    return (
        // --- Full JSX structure remains the same ---
        <div className="max-w-5xl mx-auto p-4 sm:p-6">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6">
                 {/* ... */}
                 <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2 sm:mb-0 flex items-center">
                    <FontAwesomeIcon icon={faClipboardList} className="mr-3 h-6 w-6 text-indigo-600" />
                    Working Memory Assessments
                </h1>
                <button
                    onClick={() => navigate('/dashboard')}
                    className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline focus:outline-none focus:ring-1 focus:ring-indigo-500 rounded px-1 self-center"
                >
                    ← Back to Dashboard
                </button>
            </div>

            {/* Introduction Card */}
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-8">
                 {/* ... */}
                 <div className="sm:flex items-start">
                    <div className="hidden sm:flex h-12 w-12 bg-indigo-100 rounded-lg flex-shrink-0 mr-4 items-center justify-center">
                        <FontAwesomeIcon icon={faBrain} className="text-indigo-600 h-6 w-6" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 mb-2">Measure Your Working Memory Capacity</h2>
                        <p className="text-gray-600 mb-3">
                            These standardized complex span tasks measure your working memory capacity – the ability to
                            hold and manipulate information simultaneously. Regular assessments help track changes over time.
                        </p>
                        <div className="bg-indigo-50 border-l-4 border-indigo-400 p-3 text-sm text-indigo-800 rounded-r-md">
                            <strong>Pro Tip:</strong> Factors like stress, sleep quality, and exercise dramatically affect working memory.
                            Take assessments in similar (and ideally positive) mental and physical states for the most accurate tracking.
                        </div>
                    </div>
                </div>
            </div>

            {/* Available Tests Section */}
            <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center">
                 {/* ... */}
                 <FontAwesomeIcon icon={faClipboardList} className="mr-2 h-5 w-5 text-gray-600" />
                Available Assessments
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {/* Counting Span Card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-150 flex flex-col h-full">
                     {/* ... Full Card Content ... */}
                     <div className="p-5 flex-grow">
                        <div className="flex items-center mb-3">
                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <FontAwesomeIcon icon={faCalculator} className="text-blue-600 h-5 w-5" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">Counting Span</h3>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            Count blue circles among distractors while remembering the sequence of counts.
                            Assesses visuospatial processing and verbal storage.
                        </p>
                        <div className="bg-blue-50 p-2 rounded text-xs text-blue-800 mb-4">
                            Estimated time: 10-15 minutes
                        </div>
                    </div>
                    <div className="p-4 border-t border-gray-200 bg-gray-50">
                        <Link
                            to="/testing/counting-span"
                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                        >
                            View Assessment <FontAwesomeIcon icon={faArrowRight} className="ml-2 h-4 w-4" />
                        </Link>
                    </div>
                </div>
                {/* Operation Span Card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm opacity-70 flex flex-col h-full">
                     {/* ... Full Card Content ... */}
                     <div className="p-5 flex-grow">
                        <div className="flex items-center mb-3">
                            <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                                <FontAwesomeIcon icon={faCalculator} className="text-gray-400 h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-400">Operation Span</h3>
                                <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-full">Coming Soon</span>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                            Solve math problems while remembering letters. Assesses quantitative processing and verbal storage capacity.
                        </p>
                        <div className="bg-gray-100 p-2 rounded text-xs text-gray-500 mb-4">
                            Estimated time: 12-15 minutes
                        </div>
                    </div>
                    <div className="p-4 border-t border-gray-200 bg-gray-50">
                        <button
                            disabled
                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-400 bg-gray-100 cursor-not-allowed"
                        >
                            Coming Soon
                        </button>
                    </div>
                </div>
                {/* Reading Span Card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm opacity-70 flex flex-col h-full">
                    {/* ... Full Card Content ... */}
                    <div className="p-5 flex-grow">
                        <div className="flex items-center mb-3">
                            <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                                <FontAwesomeIcon icon={faBook} className="text-gray-400 h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-400">Reading Span</h3>
                                <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-full">Coming Soon</span>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                            Read sentences and judge their sense while remembering letters. Assesses verbal processing and storage capacity.
                        </p>
                        <div className="bg-gray-100 p-2 rounded text-xs text-gray-500 mb-4">
                            Estimated time: 10-12 minutes
                        </div>
                    </div>
                    <div className="p-4 border-t border-gray-200 bg-gray-50">
                        <button
                            disabled
                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-400 bg-gray-100 cursor-not-allowed"
                        >
                            Coming Soon
                        </button>
                    </div>
                </div>
            </div>

            {/* Resources Section */}
            <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                {/* ... Full Section Content ... */}
                <h3 className="text-lg font-semibold mb-3 text-gray-800">About Working Memory Assessments</h3>
                <p className="text-sm text-gray-600 mb-4">
                    Complex span tasks are standardized cognitive assessments used in scientific research to measure working memory capacity.
                    Unlike the adaptive Dual N-Back training, these tests provide consistent measurement benchmarks to track your progress over time.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <span className="text-blue-600 font-bold">?</span>
                        </div>
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Why take assessments?</h4>
                            <p className="text-xs text-gray-600">
                                Regular assessments provide objective measurements of your working memory capacity, helping you track your progress
                                from N-Back training over time using standardized methods.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <span className="text-blue-600 font-bold">!</span>
                        </div>
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">About the monthly limit</h4>
                            <p className="text-xs text-gray-600">
                                Assessments are limited to once per month, which is ideal for tracking progress.
                                This spacing helps prevent practice effects while still providing reliable data points
                                for monitoring your working memory improvements.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TestingPage;